const upperCaseWords = [
  'AI',
  'RAG',
  'ASCII',
  'API',
  'URL',
  'HTML',
  'XML',
  'JSON',
  'CSS',
  'JS',
  'SQL',
  'ID',
  'UI',
  'UX',
  'CDN',
  'SDK',
  'GPT',
  'MCP',
  'PCI',
  'DSS',
  'SSO',
  'MFA',
  'DLP',
  'GDPR',
  'HIPAA',
  'SOC',
  'RBAC',
  'SaaS',
  'OAuth',
  'SSN',
  'PII',
  'IP',
  'CRM',
  'AgentForce',
  'NHI',
  'MTLS',
];

const preservedCaseMap = new Map(upperCaseWords.map((w) => [w.toLowerCase(), w]));

export function beautifyCamelCaseString(value: string) {
  // Split the string into words, but avoid splitting acronyms in upperCaseWords
  const resultArray = value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').split(' ');
  const resultFixedArray = resultArray
    .map((word) => {
      if (upperCaseWords.includes(word.toUpperCase())) {
        return word.toUpperCase();
      } else {
        return word;
      }
    })
    .join(' ');

  return resultFixedArray.charAt(0).toUpperCase() + resultFixedArray.slice(1);
}

export function toSentenceCase(value: string): string {
  const restored = value.toLowerCase().replace(/\b[a-z]+\b/g, (word) => {
    return preservedCaseMap.get(word) ?? word;
  });
  return restored.charAt(0).toUpperCase() + restored.slice(1);
}

export function capitalizeWord(value: string): string {
  const preserved = preservedCaseMap.get(value.toLowerCase());
  if (preserved) {
    return preserved;
  }
  if (value.length <= 1) {
    return value.charAt(0).toUpperCase();
  }
  const lower = value.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export const toKebab = (s: string): string => s.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

export const humanize = (s: string): string =>
  s
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const emptyToUndef = (v?: string): string | undefined => {
  const s = v?.trim();
  return s ? s : undefined;
};

export const extractEmail = (v?: string): string | undefined => {
  const s = v?.trim();
  if (!s) return undefined;
  const match = s.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/);
  return match ? match[0] : undefined;
};

export function formatFileSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Converts snake_case, UPPER_SNAKE_CASE, or ALL_CAPS string to human-readable display string.
 * Already-humanized mixed-case titles (no underscores, not all-caps) are returned as-is.
 * Examples:
 *   'UNTRUSTED_EXTERNAL_CONTENT' -> 'Untrusted External Content'
 *   'data_exfiltration'          -> 'Data Exfiltration'
 *   'MALWARE'                    -> 'Malware'
 *   'Already Humanized'          -> 'Already Humanized'
 */
export function formatEvidenceLabel(key: string): string {
  if (key.includes('_')) {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  if (key === key.toUpperCase() && key.length > 1) {
    return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
  }
  return key;
}

/**
 * Safely parses Evidence.additionalData which may arrive as a JSON string from the API.
 * Returns the parsed object, or undefined if parsing fails, input is falsy, or result is not a plain object.
 */
export function parseAdditionalData(
  additionalData: string | Record<string, string> | undefined
): Record<string, unknown> | undefined {
  if (!additionalData) return undefined;

  let result: unknown;
  if (typeof additionalData === 'string') {
    try {
      result = JSON.parse(additionalData);
    } catch {
      return undefined;
    }
  } else {
    result = additionalData;
  }

  if (typeof result !== 'object' || result === null || Array.isArray(result)) {
    return undefined;
  }
  return result as Record<string, unknown>;
}

/**
 * Ensures a URL has a protocol prefix (https:// by default).
 * Returns empty string if URL is null/undefined/empty.
 * @param url - The URL string to normalize
 * @returns URL with protocol prefix
 */
export function ensureUrlProtocol(url: string | null | undefined): string {
  if (!url) {
    return '';
  }
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
}

/**
 * Truncates a URL string to a specified maximum length with ellipsis.
 * Returns empty string if URL is null/undefined/empty.
 * @param url - The URL string to truncate
 * @param maxLength - Maximum length before truncation (default: 40)
 * @returns Truncated URL with '...' suffix if exceeds maxLength
 */
export function truncateUrl(url: string | null | undefined, maxLength = 40): string {
  if (!url) {
    return '';
  }
  if (url.length <= maxLength) {
    return url;
  }
  return url.substring(0, maxLength) + '...';
}
